import {
	recoverOnboardingCalibration,
	type OnboardingCalibrationContinuation
} from '$lib/apis/content-factory/onboarding';
import type { Onboarding1FlowState } from '$lib/components/content-factory/onboarding/onboarding1Flow';
import { assertOnboardingCalibrationContinuation } from './onboardingCalibrationContinuation';
import type { UnknownCalibrationRecoveryAction } from './onboardingUnknownRecovery';

export const executeUnknownCalibrationRecovery = async ({
	token,
	flow,
	conversationId,
	current,
	retry,
	signal,
	isCurrent,
	onGenerationStarted,
	recover = recoverOnboardingCalibration
}: {
	token: string;
	flow: Onboarding1FlowState;
	conversationId: string;
	current: OnboardingCalibrationContinuation;
	retry?: {
		operation_ref: string;
		expected_attempt: number;
		recoveryAction?: UnknownCalibrationRecoveryAction;
	};
	signal?: AbortSignal;
	isCurrent?: () => boolean;
	onGenerationStarted?: () => void | Promise<void>;
	recover?: typeof recoverOnboardingCalibration;
}): Promise<OnboardingCalibrationContinuation> => {
	assertOnboardingCalibrationContinuation(current, flow, conversationId);
	if (
		!retry?.recoveryAction ||
		current.status !== 'unknown' ||
		retry.expected_attempt !== current.attempt
	)
		return current;
	if (retry.operation_ref !== current.operation_ref)
		throw new Error('Onboarding recovery scope changed');
	const requireOwner = () => {
		if (signal?.aborted || isCurrent?.() === false)
			throw new Error('Onboarding recovery view changed');
	};
	requireOwner();
	if (retry.recoveryAction === 'regenerate_unknown') await onGenerationStarted?.();
	requireOwner();
	const result = await recover(
		token,
		flow.discoveryRunId,
		{
			conversation_id: conversationId,
			operation_ref: current.operation_ref,
			expected_attempt: current.attempt,
			action: retry.recoveryAction,
			acknowledge_possible_duplicate_work: retry.recoveryAction === 'regenerate_unknown'
		},
		{ signal }
	);
	requireOwner();
	return assertOnboardingCalibrationContinuation(result, flow, conversationId);
};
